import { lerp, slerp, type Quat, type Vec3 } from '../sim/mathx';
import type { TrackState } from '../sim/trackTypes';
import type { TelemetrySnapshot } from './telemetryBuffer';

export const DEFAULT_EXTRAPOLATION_HORIZON = 0.1;

export interface InterpolationOptions {
  /** Maximum render-time extrapolation beyond the newest snapshot, in seconds. */
  extrapolationHorizon?: number;
}

export interface InterpolatedTelemetry {
  /** Effective (possibly clamped) render time, in simulation seconds. */
  time: number;
  scout: { posEnu: Vec3; attitude: Quat };
  targets: Array<{ id: string; posEnu: Vec3 }>;
  tracks: Array<{ id: number; state: TrackState; posEnu: Vec3 }>;
}

/** Interpolate matching entities and briefly extrapolate from the last two frames. */
export const interpolateTelemetry = (
  previous: TelemetrySnapshot,
  latest: TelemetrySnapshot,
  renderTimestamp: number,
  options: InterpolationOptions = {},
): InterpolatedTelemetry => {
  const horizon = options.extrapolationHorizon ?? DEFAULT_EXTRAPOLATION_HORIZON;
  if (!Number.isFinite(horizon) || horizon < 0) {
    throw new RangeError('extrapolationHorizon must be finite and >= 0');
  }
  if (!Number.isFinite(renderTimestamp)) throw new RangeError('renderTimestamp must be finite');

  const interval = latest.time - previous.time;
  const effectiveTime = Math.min(Math.max(renderTimestamp, previous.time), latest.time + horizon);
  const alpha = interval > 0 ? (effectiveTime - previous.time) / interval : 1;
  const previousTargets = new Map(previous.targets.map((target) => [target.id, target]));
  const previousTracks = new Map(previous.tracks.map((track) => [track.id, track]));

  return {
    time: interval > 0 ? effectiveTime : latest.time,
    scout: {
      posEnu:
        interval > 0
          ? lerp(previous.scout.posEnu, latest.scout.posEnu, alpha)
          : { ...latest.scout.posEnu },
      attitude:
        interval > 0
          ? slerp(previous.scout.attitude, latest.scout.attitude, alpha)
          : { ...latest.scout.attitude },
    },
    targets: latest.targets.map((target) => {
      const before = previousTargets.get(target.id);
      return {
        id: target.id,
        posEnu:
          before && interval > 0 ? lerp(before.posEnu, target.posEnu, alpha) : { ...target.posEnu },
      };
    }),
    tracks: latest.tracks.map((track) => {
      const before = previousTracks.get(track.id);
      return {
        id: track.id,
        state: track.state,
        posEnu:
          before && interval > 0 ? lerp(before.posEnu, track.posEnu, alpha) : { ...track.posEnu },
      };
    }),
  };
};
